import { Injectable } from '@angular/core'
import { createStore, withProps } from '@ngneat/elf'
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state'
import { Apollo, gql } from 'apollo-angular'
import { NzNotificationService } from 'ng-zorro-antd/notification'
import { map, tap } from 'rxjs'
import {
  AuthenticateGQL,
  AuthenticationInput,
  MutationRequestPasswordResetArgs,
} from '../gql/generated'
import { LoaderService } from './loader.service'

interface AuthenticationState {
  isLoggedIn: boolean
  identifier: string
  pluginName: string
  resolveUrl: string
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  authStore$ = createStore(
    { name: 'authentication' },
    withProps<AuthenticationState>({
      isLoggedIn: false,
      identifier: '',
      pluginName: '',
      resolveUrl: '',
    })
  )

  public isLoggedIn$ = this.authStore$.pipe(map((store) => store.isLoggedIn))

  constructor(
    private nzNotificationService: NzNotificationService,
    private authenticateGQL: AuthenticateGQL,
    private loaderService: LoaderService,
    private apollo: Apollo
  ) {
    persistState(this.authStore$, {
      key: 'auth',
      storage: localStorageStrategy,
    })
  }

  login(input: AuthenticationInput) {
    return this.loaderService.withLoader(() => {
      return this.authenticateGQL
        .mutate(
          {
            input,
          },
          {
            context: {
              headers: {
                'vendure-token': 'K7',
              },
            },
          }
        )
        .pipe(
          tap({
            next: (response) => {
              if (response.data?.authenticate.__typename === 'CurrentUser') {
                const currentUser = response.data?.authenticate
                this.authStore$.update((store) => ({
                  ...store,
                  isLoggedIn: true,
                  identifier: currentUser.identifier,
                  pluginName: input.apple_customer
                    ? 'apple'
                    : input.discord_customer
                      ? 'discord'
                      : input.facebook_customer
                        ? 'facebook'
                        : input.google_customer
                          ? 'google'
                          : input.microsoft_customer
                            ? 'microsoft'
                            : '',
                }))
                this.nzNotificationService.success(
                  'Login successful',
                  'You have been logged in successfully'
                )
              }

              if (
                response.data?.authenticate.__typename ===
                'InvalidCredentialsError'
              ) {
                this.nzNotificationService.error(
                  'Invalid credential',
                  response.data?.authenticate?.message
                )
              }

              if (
                response.data?.authenticate.__typename === 'NotVerifiedError'
              ) {
                this.nzNotificationService.error(
                  'Not verified',
                  response.data?.authenticate?.message
                )
              }

              return response
            },
          })
        )
    }, AuthenticationService.name)
  }

  forgotPassword(emailAddress: MutationRequestPasswordResetArgs) {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation RequestPasswordReset($emailAddress: String!) {
            requestPasswordReset(emailAddress: $emailAddress) {
              ... on NativeAuthStrategyError {
                errorCode
                message
              }
              ... on Success {
                success
              }
            }
          }
        `,
        variables: {
          emailAddress: emailAddress.emailAddress,
        },
      })
      .pipe(
        tap({
          next: (response: { data?: any }) => {
            console.log(response.data?.requestPasswordReset)
            if (response.data?.requestPasswordReset.__typename === 'Success') {
              this.nzNotificationService.success(
                'Success',
                `Check your email: ${emailAddress.emailAddress} to reset your password`
              )
            }
          },
          error: (err) => {
            console.error('GraphQL error:', err)
          },
        })
      )
  }
}
